/**
 * BeforeAfterPhotoSlots — two fixed, labelled before/after photo slots on the
 * service-entry add form (spec 011, Slice 1).
 *
 * Reuses the existing album photo pipeline end to end — PhotoCapture
 * (single-file mode), the photoQueue/usePhotoQueue offline queue,
 * PhotoTile/PhotoLightbox for viewing, PhotoPublishModal for the redaction
 * preview/confirm gate, and PhotoDeleteDialog for crypto-shred delete. This
 * component does not talk to gg-api directly for uploads — it only tags
 * queue items with `role`/`serviceEntryDraftId` (see photoQueue.ts) so the
 * drain in usePhotoQueue.ts can later resolve them onto the matching
 * service-queue entry. See useServiceQueue.ts's `resolvePendingPhotoRefs`
 * for the other half of that handshake.
 *
 * State matrix: none / one-only / both / uploading / offline (queued) /
 * loading-error. Reads from local previewUrl/photoQueue are first-class,
 * never "stale" — matches the rest of the app's offline-UX doctrine.
 *
 * Spec 011 Slice 2 — EXIF-duration suggestion: `handlePick` reads
 * `DateTimeOriginal` from each picked photo's ORIGINAL bytes (before any
 * HEIC->JPEG transcode, which does not preserve EXIF — see
 * `readExifCapturedAtMs`'s doc) entirely client-side (FR-9, works offline);
 * the two timestamps are combined into a `DurationSuggestion` tri-state
 * (`pending` / `unavailable` / `suggested`) and passed up via `onChange`.
 * This component only computes the raw suggestion — the prefill, the
 * accepted-vs-edited provenance tracking, and the "couldn't estimate" note
 * all live in the consuming form (ServiceLog.tsx), directly beneath the
 * Labor hours field (eames' design: the suggestion is a quiet provenance
 * line under that field, not a photos-panel notice). Absolute capture times
 * never leave this component — only the computed suggestion (a duration) or
 * "no suggestion" ever crosses that boundary.
 *
 * Deliberately NOT included in Slice 1 (scoping call, flagged in the PR):
 * the general album grid already exposes Publish/Make-private for any
 * committed photo (before/after photos land in that same album), so this
 * compact two-slot form UI keeps only view (Lightbox) + Delete (crypto-shred,
 * so a mis-attached photo can be corrected before saving) + Swap.
 *
 * lamport's revised DTO: the client sends a plain committed photoId
 * (`beforePhotoId`/`afterPhotoId`), never a client-built ref — the server
 * resolves the id to its authoritative {photoId, sha256} internally, so this
 * component only ever needs to know whether a photo has reached
 * `status === 'committed'`, never its sha256.
 */

import { Button } from '@/components/Button';
import { F } from '@/design/fonts';
import { RADIUS, SPACE, T } from '@/design/tokens';
import { useAuth } from '@/features/auth/useAuth';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { usePhotoQueue } from '@/hooks/usePhotoQueue';
import { usePhotos, type Photo } from '@/hooks/usePhotos';
import { ensureJpeg } from '@/lib/heicTranscode';
import {
  evaluateDurationSuggestion,
  readExifCapturedAtMs,
  type DurationSuggestion,
} from '@/lib/laborHoursSuggestion';
import {
  enqueue,
  listAll as listPhotoQueueAll,
  subscribe as subscribePhotoQueue,
  update as updatePhotoQueueItem,
} from '@/lib/photoQueue';
import { ArrowLeftRight } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { PhotoCapture } from './PhotoCapture';
import { PhotoDeleteDialog } from './PhotoDeleteDialog';
import { PhotoLightbox } from './PhotoLightbox';
import { PhotoTile, type CommittedPhoto, type PhotoTileItem } from './PhotoTile';

export type PhotoRole = 'before' | 'after';

/** What a slot currently references — passed up so the caller can build the
 * POST body (once `committed`) or a serviceQueue `pendingPhotoRefs` entry. */
export interface SlotRef {
  /** Still in photoQueue, not yet uploaded+committed. */
  clientId?: string;
  /** Uploaded+committed at the storage layer; may still be pending the
   * async strip-worker (see `committed`). */
  photoId?: string;
  /** True once `usePhotos` shows this photoId as `status === 'committed'` —
   * only then is it safe to send `photoId` in the POST body directly. */
  committed?: boolean;
}

export interface BeforeAfterPhotoValue {
  before: SlotRef | null;
  after: SlotRef | null;
  /** Spec 011 Slice 2 — see `laborHoursSuggestion.ts`'s `DurationSuggestion`. */
  suggestion: DurationSuggestion;
}

interface BeforeAfterPhotoSlotsProps {
  vehicleId: string;
  /** Correlates picked photos to the in-progress entry — see photoQueue.ts. */
  draftId: string;
  disabled?: boolean;
  onChange: (value: BeforeAfterPhotoValue) => void;
}

interface SlotIdState {
  clientId: string | null;
  photoId: string | null;
}

const EMPTY_SLOT: SlotIdState = { clientId: null, photoId: null };

const ROLE_LABEL: Record<PhotoRole, string> = { before: 'Before', after: 'After' };

/**
 * Watches the RAW (unfiltered) photoQueue storage for `clientId`'s photoId —
 * usePhotoQueue's `items` filters out 'inflight' items (the exact moment
 * photoId gets set during upload), so that hook alone can't observe this
 * transition. Once captured, the photoId is kept in state even after the
 * queue item is later removed (successful drain), which is what lets the
 * slot resolve to a committed photo without needing the drain to still be
 * "visible" to this component.
 */
function useResolvedPhotoId(clientId: string | null): string | null {
  const [photoId, setPhotoId] = useState<string | null>(null);

  useEffect(() => {
    setPhotoId(null);
    if (!clientId) return;

    let cancelled = false;
    async function check() {
      const all = await listPhotoQueueAll();
      const item = all.find((i) => i.clientId === clientId);
      if (!cancelled && item?.photoId) setPhotoId(item.photoId);
    }
    void check();
    const unsubscribe = subscribePhotoQueue(() => void check());
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [clientId]);

  return photoId;
}

const slotBoxStyle: CSSProperties = {
  aspectRatio: '1 / 1',
  width: '100%',
};

// `photoRole` (not `role`) — a component prop literally named `role` trips
// eslint-plugin-jsx-a11y's aria-role rule, which doesn't distinguish a
// custom component prop from the DOM `role` attribute.
function SlotLabel({ photoRole }: { photoRole: PhotoRole }) {
  return (
    <p
      style={{
        margin: `0 0 ${SPACE.xs}px`,
        fontSize: 11,
        fontFamily: F.mono,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: T.textMuted,
      }}
    >
      {ROLE_LABEL[photoRole]}
    </p>
  );
}

export function BeforeAfterPhotoSlots({
  vehicleId,
  draftId,
  disabled = false,
  onChange,
}: BeforeAfterPhotoSlotsProps) {
  const { user } = useAuth();
  const isOnline = useOnlineStatus();
  const [before, setBefore] = useState<SlotIdState>(EMPTY_SLOT);
  const [after, setAfter] = useState<SlotIdState>(EMPTY_SLOT);
  // Spec 011 Slice 2 — EXIF `DateTimeOriginal`, read at pick time, per slot.
  // `undefined` = no photo in this slot yet (or still reading); `null` =
  // read completed with no usable timestamp. Never sent to the server —
  // see `evaluateDurationSuggestion`/`readExifCapturedAtMs`.
  const [beforeExifMs, setBeforeExifMs] = useState<number | null | undefined>(undefined);
  const [afterExifMs, setAfterExifMs] = useState<number | null | undefined>(undefined);
  const [lightboxItem, setLightboxItem] = useState<PhotoTileItem | null>(null);
  const [deleteDialogPhotoId, setDeleteDialogPhotoId] = useState<string | null>(null);

  const { items: queueItems, drain, retry, discard } = usePhotoQueue(vehicleId);
  const { data: photos } = usePhotos(vehicleId);

  const beforeResolvedPhotoId = useResolvedPhotoId(before.clientId);
  const afterResolvedPhotoId = useResolvedPhotoId(after.clientId);
  const beforePhotoId = before.photoId ?? beforeResolvedPhotoId;
  const afterPhotoId = after.photoId ?? afterResolvedPhotoId;

  const beforeQueueItem = before.clientId
    ? (queueItems.find((q) => q.clientId === before.clientId) ?? null)
    : null;
  const afterQueueItem = after.clientId
    ? (queueItems.find((q) => q.clientId === after.clientId) ?? null)
    : null;

  const beforeCommitted = beforePhotoId
    ? (photos?.find((p) => p.photoId === beforePhotoId) ?? null)
    : null;
  const afterCommitted = afterPhotoId
    ? (photos?.find((p) => p.photoId === afterPhotoId) ?? null)
    : null;

  // Notify the parent form of the current best-known ref for each slot,
  // whenever any of the underlying resolution state changes.
  useEffect(() => {
    const toRef = (
      idState: SlotIdState,
      resolvedPhotoId: string | null,
      photo: Photo | null,
    ): SlotRef | null => {
      if (!idState.clientId && !resolvedPhotoId) return null;
      if (resolvedPhotoId && photo?.status === 'committed') {
        return { photoId: resolvedPhotoId, committed: true };
      }
      if (resolvedPhotoId) return { photoId: resolvedPhotoId };
      if (idState.clientId) return { clientId: idState.clientId };
      return null;
    };
    onChange({
      before: toRef(before, beforePhotoId, beforeCommitted),
      after: toRef(after, afterPhotoId, afterCommitted),
      suggestion: evaluateDurationSuggestion(beforeExifMs, afterExifMs),
    });
    // onChange identity isn't stable across parent renders in the common
    // case (inline handler) — depending only on the resolution inputs avoids
    // re-notifying on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    before,
    after,
    beforePhotoId,
    afterPhotoId,
    beforeCommitted,
    afterCommitted,
    beforeExifMs,
    afterExifMs,
  ]);

  const handlePick = useCallback(
    async (role: PhotoRole, files: File[]) => {
      const file = files[0];
      if (!file || !user) return;

      // Spec 011 Slice 2 — kick off the EXIF read now, from the ORIGINAL
      // file BEFORE any HEIC->JPEG transcode below (heic2any's canvas
      // re-encode does not preserve EXIF, so reading afterward would always
      // return null). Deliberately NOT awaited inline: the read is
      // independent of the upload path and only ever updates the
      // suggestion state (never sent to the server, never blocks
      // enqueue/drain) — see readExifCapturedAtMs, which never throws.
      void readExifCapturedAtMs(file).then((exifMs) => {
        if (role === 'before') setBeforeExifMs(exifMs);
        else setAfterExifMs(exifMs);
      });

      let toUpload: File;
      try {
        toUpload = await ensureJpeg(file);
      } catch {
        // HEIC decode failed — surfaced the same way PhotoCapture surfaces
        // any other rejected file, rather than silently dropping the pick.
        window.alert("Couldn't process this photo. Try a different photo, or export it as JPEG.");
        return;
      }

      const bytes = await toUpload.arrayBuffer();
      const clientId = crypto.randomUUID();
      const capturedAt = new Date(toUpload.lastModified).toISOString();

      await enqueue(
        {
          clientId,
          uid: user.uid,
          vehicleId,
          contentType: toUpload.type || 'image/jpeg',
          sizeBytes: toUpload.size,
          capturedAt,
          queuedAt: new Date().toISOString(),
          status: 'pending',
          role,
          serviceEntryDraftId: draftId,
        },
        bytes,
      );

      if (role === 'before') setBefore({ clientId, photoId: null });
      else setAfter({ clientId, photoId: null });

      void drain();
    },
    [user, vehicleId, draftId, drain],
  );

  const handleSwap = useCallback(async () => {
    const prevBefore = before;
    const prevAfter = after;
    setBefore(prevAfter);
    setAfter(prevBefore);
    // Spec 011 Slice 2 — the EXIF timestamps travel with their photo, not
    // their slot; swap them the same way the slot ids themselves swap.
    const prevBeforeExifMs = beforeExifMs;
    const prevAfterExifMs = afterExifMs;
    setBeforeExifMs(prevAfterExifMs);
    setAfterExifMs(prevBeforeExifMs);
    // Re-tag any still-queued (not yet uploaded) item so its eventual
    // write-back in usePhotoQueue.ts's drain lands under the correct,
    // now-swapped role.
    if (prevBefore.clientId) await updatePhotoQueueItem(prevBefore.clientId, { role: 'after' });
    if (prevAfter.clientId) await updatePhotoQueueItem(prevAfter.clientId, { role: 'before' });
  }, [before, after, beforeExifMs, afterExifMs]);

  const canSwap = !disabled && (before.clientId !== null || after.clientId !== null);

  function handleOpen(role: PhotoRole) {
    const committed = role === 'before' ? beforeCommitted : afterCommitted;
    const queueItem = role === 'before' ? beforeQueueItem : afterQueueItem;
    if (committed) {
      const tile: CommittedPhoto = {
        photoId: committed.photoId,
        url: committed.url,
        contentType: committed.contentType,
        status: 'committed',
        createdAt: committed.createdAt,
      };
      if (committed.capturedAt !== undefined) tile.capturedAt = committed.capturedAt;
      if (committed.caption !== undefined) tile.caption = committed.caption;
      setLightboxItem(tile);
    } else if (queueItem) {
      setLightboxItem({ ...queueItem, type: 'queued' });
    }
  }

  function renderSlot(role: PhotoRole) {
    const idState = role === 'before' ? before : after;
    const queueItem = role === 'before' ? beforeQueueItem : afterQueueItem;
    const resolvedPhotoId = role === 'before' ? beforePhotoId : afterPhotoId;
    const committed = role === 'before' ? beforeCommitted : afterCommitted;
    const isEmpty = !idState.clientId && !resolvedPhotoId;

    if (isEmpty) {
      return (
        <div style={slotBoxStyle}>
          <PhotoCapture
            onFiles={(files) => void handlePick(role, files)}
            disabled={disabled}
            multiple={false}
            ariaLabel={`Add ${ROLE_LABEL[role].toLowerCase()} photo`}
            label={`Add ${ROLE_LABEL[role].toLowerCase()} photo`}
          />
        </div>
      );
    }

    if (queueItem) {
      const isFailed = queueItem.status === 'failed';
      return (
        <div style={{ ...slotBoxStyle, position: 'relative' }}>
          <PhotoTile item={{ ...queueItem, type: 'queued' }} onOpen={() => handleOpen(role)} />
          {isFailed ? (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: SPACE.xs,
                background: 'rgba(0,0,0,0.55)',
                borderRadius: RADIUS.md,
                padding: SPACE.xs,
              }}
            >
              <Button
                variant="secondary"
                size="sm"
                aria-label={`Retry ${ROLE_LABEL[role].toLowerCase()} photo upload`}
                onClick={() => void retry(queueItem.clientId)}
              >
                Retry
              </Button>
              <button
                type="button"
                aria-label={`Discard ${ROLE_LABEL[role].toLowerCase()} photo`}
                onClick={() => {
                  void discard(queueItem.clientId);
                  if (role === 'before') {
                    setBefore(EMPTY_SLOT);
                    setBeforeExifMs(undefined);
                  } else {
                    setAfter(EMPTY_SLOT);
                    setAfterExifMs(undefined);
                  }
                }}
                style={{
                  fontSize: 11,
                  fontFamily: F.body,
                  fontWeight: 600,
                  background: 'transparent',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.5)',
                  borderRadius: RADIUS.sm,
                  padding: `${SPACE.xs}px ${SPACE.sm}px`,
                  cursor: 'pointer',
                  minHeight: 44,
                }}
              >
                Discard
              </button>
            </div>
          ) : null}
        </div>
      );
    }

    if (committed) {
      const tile: CommittedPhoto = {
        photoId: committed.photoId,
        url: committed.url,
        contentType: committed.contentType,
        status: 'committed',
        createdAt: committed.createdAt,
      };
      if (committed.capturedAt !== undefined) tile.capturedAt = committed.capturedAt;
      if (committed.caption !== undefined) tile.caption = committed.caption;
      return (
        <div style={{ ...slotBoxStyle, position: 'relative' }}>
          <PhotoTile item={tile} onOpen={() => handleOpen(role)} />
          <button
            type="button"
            className="clipped-focus"
            aria-label={`Remove ${ROLE_LABEL[role].toLowerCase()} photo`}
            onClick={() => setDeleteDialogPhotoId(committed.photoId)}
            style={{
              position: 'absolute',
              top: SPACE.xs,
              right: SPACE.xs,
              zIndex: 2,
              minWidth: 44,
              minHeight: 44,
              background: 'rgba(0,0,0,0.55)',
              color: '#fff',
              border: 'none',
              borderRadius: RADIUS.md,
              fontSize: 11,
              fontFamily: F.body,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Remove
          </button>
        </div>
      );
    }

    // Uploaded (photoId known) but not yet visible via GET /v1/photos — the
    // async strip-worker may still be running, or the list just hasn't
    // caught up yet. Not an error; a quiet "still working on it" state.
    return (
      <div
        style={{
          ...slotBoxStyle,
          border: `1px solid ${T.border}`,
          borderRadius: RADIUS.md,
          background: T.card,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        role="status"
        aria-label={`${ROLE_LABEL[role]} photo uploaded, preparing…`}
      >
        <span style={{ fontSize: 11, color: T.textMuted, fontFamily: F.body, padding: SPACE.xs }}>
          Uploaded — preparing…
        </span>
      </div>
    );
  }

  // Offline + queued notice — matches PhotoAlbumSection's tone/copy.
  const pendingCount = [beforeQueueItem, afterQueueItem].filter(
    (q) => q?.status === 'pending',
  ).length;

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: SPACE.md,
          alignItems: 'start',
        }}
      >
        <div>
          <SlotLabel photoRole="before" />
          {renderSlot('before')}
        </div>
        <div>
          <SlotLabel photoRole="after" />
          {renderSlot('after')}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: SPACE.sm }}>
        <Button
          variant="secondary"
          size="sm"
          disabled={!canSwap}
          onClick={() => void handleSwap()}
          aria-label="Swap before and after photos"
        >
          <ArrowLeftRight size={14} aria-hidden="true" />
          Swap
        </Button>
      </div>

      {!isOnline && pendingCount > 0 ? (
        <p
          role="status"
          aria-live="polite"
          style={{
            margin: `${SPACE.sm}px 0 0`,
            fontSize: 12,
            fontFamily: F.body,
            color: T.orange,
            textAlign: 'center',
          }}
        >
          {pendingCount === 2
            ? 'Both photos are waiting to upload. Will send when back online.'
            : 'One photo is waiting to upload. Will send when back online.'}
        </p>
      ) : null}

      <PhotoLightbox item={lightboxItem} onClose={() => setLightboxItem(null)} />

      {deleteDialogPhotoId ? (
        <PhotoDeleteDialog
          photoId={deleteDialogPhotoId}
          vehicleId={vehicleId}
          onClose={() => setDeleteDialogPhotoId(null)}
          onDeleted={() => {
            if (deleteDialogPhotoId === beforePhotoId) {
              setBefore(EMPTY_SLOT);
              setBeforeExifMs(undefined);
            }
            if (deleteDialogPhotoId === afterPhotoId) {
              setAfter(EMPTY_SLOT);
              setAfterExifMs(undefined);
            }
            setDeleteDialogPhotoId(null);
          }}
        />
      ) : null}
    </div>
  );
}
